import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button, InputGroup, InputGroupText, Spinner } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

// 🔥 MUI Date Picker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';

const CreditNoteUpdate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isLoaded, setIsLoaded] = useState(false);
    const [reductionType, setReductionType] = useState("amount");
    const [clients, setClients] = useState([]);
    const [pendingInvoices, setPendingInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedClientOpt, setSelectedClientOpt] = useState(null);
    
    const today = dayjs();

    const [formData, setFormData] = useState({
        id: id,
        invoice_id: "",
        credit_note_no: "",
        date: today,
        amount: "",
        reduced_kg: "",
        gst_percentage: 0,
        reason: ""
    });

    useEffect(() => {
        const init = async () => {
            dispatch(loadingTrue());
            await fetchClients();
            await fetchCreditNoteDetails();
            dispatch(loadingFalse());
        };
        init();
    }, [id]);

    const fetchClients = async () => {
        try {
            const res = await Handler({ method: "GET", url: "/clients/list.php" });
            if (res?.success) {
                const clientOptions = res?.data?.map(c => ({
                    value: c?.id,
                    label: c?.client?.name?.toUpperCase() || "UNKNOWN CLIENT"
                })) || [];
                setClients(clientOptions);
            }
        } catch (err) { console.error("Client fetch error:", err); }
    };

    const fetchInvoices = async (clientId) => {
        try {
            const res = await Handler({ method: "GET", url: `/invoice/list.php?client_id=${clientId}` });
            if (res?.success) {
                const invOptions = res.data.map(i => ({
                    value: i.invoice.id,
                    label: `${i.invoice.invoice_no} (Total: ₹${parseFloat(i.summary.total_amount)?.toLocaleString()})`,
                    fullData: i
                }));
                setPendingInvoices(invOptions);
                return invOptions;
            }
        } catch (err) { console.error("Error loading invoices"); }
        return [];
    };

    const fetchCreditNoteDetails = async () => {
        try {
            const res = await Handler({ method: "GET", url: `/credit_note/view.php?id=${id}` });
            if (res?.success) {
                const cn = res.data.credit_note;
                const invData = res.data.invoice_current;
                const clientData = res.data.client;

                // Restored Values calculation (matching PHP logic)
                const restoredSubtotal = parseFloat(invData.remaining_subtotal) + parseFloat(cn.credit.base_amount);
                const restoredKg = parseFloat(invData.remaining_kg) + parseFloat(cn.reduced_kg);

                const clientOpt = {
                    value: clientData.id,
                    label: clientData.name.toUpperCase()
                };
                setSelectedClientOpt(clientOpt);

                await fetchInvoices(clientData.id);

                // Update selected invoice with restored values for correct preview
                setSelectedInvoice({
                    summary: { 
                        subtotal: restoredSubtotal,
                        total_amount: restoredSubtotal + parseFloat(invData.remaining_gst) // Simplified for preview
                    },
                    quantity: { kg: restoredKg }
                });

                setReductionType(cn.type);
                setFormData({
                    id: id,
                    invoice_id: invData.id,
                    credit_note_no: cn.credit_note_no,
                    date: dayjs(cn.date),
                    amount: cn.type === "amount" ? cn.credit.base_amount : "",
                    reduced_kg: cn.type === "kg" ? cn.reduced_kg : "",
                    gst_percentage: cn.credit.gst_percentage,
                    reason: cn.reason
                });
                setIsLoaded(true);
            }
        } catch (err) { toast.error("Error loading details"); }
    };

    const handleClientChange = async (clientId) => {
        setPendingInvoices([]);
        setSelectedInvoice(null);
        setSelectedClientOpt(clients.find(c => c.value === clientId));
        setFormData({ ...formData, invoice_id: "", amount: "", reduced_kg: "" });
        if (clientId) {
            dispatch(loadingTrue());
            await fetchInvoices(clientId);
            dispatch(loadingFalse());
        }
    };

    const handleTypeToggle = (type) => {
        setReductionType(type);
        setFormData({ ...formData, amount: "", reduced_kg: "" });
    };

    const calculatePreview = () => {
        if (!selectedInvoice) return { reduced: 0, gst: 0, total: 0 };
        
        // Use subtotal for rate calculation to match PHP logic
        const baseSubtotal = parseFloat(selectedInvoice.summary.subtotal || 0);
        const baseKg = parseFloat(selectedInvoice.quantity.kg || 0);
        const rate = baseKg > 0 ? baseSubtotal / baseKg : 0;
        const gstPerc = parseFloat(formData.gst_percentage || 0);

        let baseAmount = 0;
        let reducedWeight = 0;

        if (reductionType === "amount") {
            baseAmount = parseFloat(formData.amount || 0);
            reducedWeight = rate > 0 ? baseAmount / rate : 0;
        } else {
            reducedWeight = parseFloat(formData.reduced_kg || 0);
            baseAmount = reducedWeight * rate;
        }

        const gstAmt = (baseAmount * gstPerc) / 100;
        const totalCredit = baseAmount + gstAmt;

        return {
            reduced_val: reductionType === "amount" ? reducedWeight.toFixed(2) : baseAmount.toFixed(2),
            gst: gstAmt.toFixed(2),
            total: totalCredit.toFixed(2)
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.credit_note_no || !formData.invoice_id) return toast.error("Required fields missing!");

        const result = await Swal.fire({
            title: 'Update Credit Note?',
            text: "This will recalculate invoice totals. Old invoice data will be adjusted.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#007bff',
            confirmButtonText: 'Yes, Update'
        });

        if (result.isConfirmed) {
            dispatch(loadingTrue());
            try {
                const submitData = {
                    id: formData.id,
                    invoice_id: formData.invoice_id,
                    credit_note_no: formData.credit_note_no,
                    date: formData.date.format('YYYY-MM-DD'),
                    reason: formData.reason,
                    gst_percentage: formData.gst_percentage,
                    ...(reductionType === "amount" ? { amount: formData.amount } : { reduced_kg: formData.reduced_kg })
                };

                const res = await Handler({ method: "POST", url: "/credit_note/update.php", data: submitData });
                if (res?.success) {
                    await Swal.fire("Updated!", "Credit note updated successfully.", "success");
                    navigate("/creditnotes");
                }
            } catch (err) { 
                toast.error(err.response?.data?.message || "Update failed"); 
            } finally { dispatch(loadingFalse()); }
        }
    };

    if (!isLoaded) return <div className="p-5 text-center"><Spinner color="primary" /></div>;
    const preview = calculatePreview();

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-1 bg-light min-vh-100">
                <div className="d-flex justify-content-between align-items-center mb-4 p-2">
                    <h3 className="fw-bold text-primary m-0">Edit Credit Note</h3>
                    <Button color="secondary" outline size="sm" onClick={() => navigate(-1)}>Back</Button>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Row className="g-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-4 mb-3">
                                <CardBody className="p-4">
                                    <h6 className="fw-bold text-muted text-uppercase small mb-4">Credit Note Details</h6>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold">Credit Note No <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="text"
                                                    value={formData.credit_note_no}
                                                    onChange={(e) => setFormData({ ...formData, credit_note_no: e.target.value })}
                                                    required
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="d-flex flex-column">
                                                <Label className="small fw-bold">Date <span className="text-danger">*</span></Label>
                                                <DesktopDatePicker
                                                    value={formData.date}
                                                    maxDate={today}
                                                    onChange={(newValue) => setFormData({ ...formData, date: newValue })}
                                                    slotProps={{ textField: { size: "small", required: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px' } } } }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-4">
                                <CardBody className="p-4">
                                    <h6 className="fw-bold text-muted text-uppercase small mb-4">Source Information</h6>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold">Select Client</Label>
                                                <Select
                                                    options={clients}
                                                    value={selectedClientOpt} 
                                                    onChange={(opt) => handleClientChange(opt?.value)}
                                                    placeholder="Search Client..."
                                                    isClearable
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold">Select Invoice</Label>
                                                <Select
                                                    options={pendingInvoices}
                                                    isDisabled={!pendingInvoices.length}
                                                    value={pendingInvoices.find(i => i.value === formData.invoice_id)}
                                                    onChange={(opt) => {
                                                        setFormData({ ...formData, invoice_id: opt?.value });
                                                        setSelectedInvoice(opt?.fullData);
                                                    }}
                                                    placeholder="Choose Invoice..."
                                                    isClearable
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label className="small fw-bold">GST (%)</Label>
                                                <Input
                                                    type="number"
                                                    value={formData.gst_percentage}
                                                    onChange={(e) => setFormData({ ...formData, gst_percentage: e.target.value })}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={8}>
                                            <FormGroup>
                                                <Label className="small fw-bold">Reason</Label>
                                                <Input
                                                    type="text"
                                                    value={formData.reason}
                                                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                                    required
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-4 sticky-top" style={{ top: "20px" }}>
                                <CardBody className="p-4">
                                    <h6 className="fw-bold mb-3 text-uppercase small text-muted">Adjustment Method</h6>
                                    <div className="d-flex mb-4 p-1 bg-light rounded-pill border">
                                        <Button color={reductionType === "amount" ? "primary" : "light"} className="flex-fill rounded-pill border-0 py-2 btn-sm fw-bold" onClick={() => handleTypeToggle("amount")}>By Amount</Button>
                                        <Button color={reductionType === "kg" ? "primary" : "light"} className="flex-fill rounded-pill border-0 py-2 btn-sm fw-bold" onClick={() => handleTypeToggle("kg")}>By Weight</Button>
                                    </div>

                                    {selectedInvoice && (
                                        <div className="bg-light p-3 rounded-3 mb-4 border">
                                            <div className="d-flex justify-content-between mb-1 small text-muted">
                                                <span>Restore Subtotal:</span>
                                                <span className="fw-bold text-dark">₹{parseFloat(selectedInvoice.summary.subtotal).toLocaleString()}</span>
                                            </div>
                                            <div className="d-flex justify-content-between small text-muted">
                                                <span>Restore Qty:</span>
                                                <span className="fw-bold text-dark">{selectedInvoice.quantity.kg} KG</span>
                                            </div>
                                        </div>
                                    )}

                                    <FormGroup className="mb-4">
                                        <Label className="small fw-bold text-primary">{reductionType === "amount" ? "REDUCTION AMOUNT (₹)" : "REDUCTION WEIGHT (KG)"}</Label>
                                        <InputGroup size="lg">
                                            <InputGroupText className="bg-white text-primary fw-bold">{reductionType === "amount" ? "₹" : "KG"}</InputGroupText>
                                            <Input
                                                type="number"
                                                required
                                                step="any"
                                                value={reductionType === "amount" ? formData.amount : formData.reduced_kg}
                                                onChange={(e) => setFormData({ ...formData, [reductionType === "amount" ? "amount" : "reduced_kg"]: e.target.value })}
                                            />
                                        </InputGroup>
                                    </FormGroup>

                                    {selectedInvoice && (formData.amount || formData.reduced_kg) && (
                                        <div className="bg-white border rounded-3 p-3 mb-4">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span>Base Credit:</span> 
                                                <span className="fw-bold">₹{reductionType === "amount" ? formData.amount : preview.reduced_val}</span>
                                            </div>
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span>GST Amount:</span> 
                                                <span className="fw-bold text-primary">₹{preview.gst}</span>
                                            </div>
                                            <hr className="my-2" />
                                            <div className="d-flex justify-content-between">
                                                <span>Total Credit:</span> 
                                                <span className="fw-bold text-success">₹{preview.total}</span>
                                            </div>
                                        </div>
                                    )}

                                    <Button color="primary" block className="py-3 fw-bold shadow-sm rounded-3" type="submit">
                                        UPDATE CREDIT NOTE
                                    </Button>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>
        </LocalizationProvider>
    );
};

export default CreditNoteUpdate;